/**
 * Helper: Query Caching
 * -----------------------------------------------------------------------------
 * A helper to handle getting and setting data to session storage.
 * @param {Object} options - options for the QueryCache function
 * @param {String} options.key - The key to use for saving data to session
 * storage.
 * @returns {Object} - exported functions to be used in components.
 */
export default function QueryCache({ key }) {
  if (!key) {
    throw new Error('Key must be set when initialising the query cache')
  }

  /**
   * Get data from the cache.
   * @returns {Object|false} - query cache data or false
   */
  const get = () => {
    try {
      const cachedData = sessionStorage.getItem(key)
        ? JSON.parse(sessionStorage.getItem(key))
        : false

      return cachedData || false

    } catch (error) {
      cnvs.ReportError('Failed to get data from cache', error)
      return false
    }
  }

  /**
   * Save data to the cache.
   * @param {Object} payload - Payload object to save to the cache
   */
  const set = (payload) => {

    // Add the current time to the payload.
    const enhancedPayload = {
      ...payload,
      updateTime: new Date(),
    }
    sessionStorage.setItem(key, JSON.stringify(enhancedPayload))
  }

  /**
   * Remove data from the cache.
   */
  const remove = () => {
    sessionStorage.removeItem(key)
  }

  /**
   * Get cache data with validation.
   * - Allows a validator function to be passed in to check that the cache data
   *   is still valid.
   * - If valid, returns the cache data.
   * - If invalid, removes the invalid data from the cache and returns false.
   * @param {Function} validator - validator function to validate the cache
   * data. Get's passed the current cache data as an argument.
   * @returns
   */
  const getWithValidation = (validator) => {
    if (typeof validator !== 'function') {
      // eslint-disable-next-line no-console
      console.error('Passed validator is not a function')
      return false
    }

    const cachedData = get()

    const isDataValid = cachedData ? validator(cachedData) : false

    if (!isDataValid) {
      remove()
      return false
    }

    return cachedData
  }

  return {
    get,
    getWithValidation,
    key,
    remove,
    set,
  }
}
